import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';
import { X } from 'react-feather';
import { FEEDBACK_REASONS } from '../constants';
import NRButtonProgress from '../../NRButtonProgress/NRButtonProgress';

export default function FeedbackForm({ onSubmit, onCancel, initialReason = '' }) {
  const theme = useTheme();
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [freeText, setFreeText] = useState(initialReason || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReasonToggle = (reason) => {
    setSelectedReasons(prev => {
      if (prev.includes(reason)) {
        return prev.filter(r => r !== reason);
      }
      return [...prev, reason];
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Combine selected reasons and free text
      const reasonParts = [];
      if (selectedReasons.length > 0) {
        reasonParts.push(selectedReasons.join('; '));
      }
      if (freeText.trim()) {
        reasonParts.push(freeText.trim());
      }
      const combinedReason = reasonParts.join(' | ');
      
      await onSubmit(combinedReason);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selectedReasons.length > 0 || freeText.trim().length > 0;
  const isOtherSelected = selectedReasons.includes(FEEDBACK_REASONS.OTHER);

  const handleCancel = () => {
    // Close form without submitting feedback
    // This reverts the optimistic thumbs down state and keeps voting available
    if (onCancel) {
      onCancel();
    }
    // No fallback - if onCancel is not provided, do nothing
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        position: 'relative'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body1">
          What went wrong?
        </Typography>
        <Button
          size="small"
          onClick={handleCancel}
          sx={{
            minWidth: 'auto',
            width: 24,
            height: 24,
            p: 0,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.5)
            }
          }}
        >
          <X size={16} />
        </Button>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        {Object.entries(FEEDBACK_REASONS).map(([key, reason]) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                size="small"
                checked={selectedReasons.includes(reason)}
                onChange={() => handleReasonToggle(reason)}
                sx={{
                  py: 0.5,
                  '& .MuiSvgIcon-root': { fontSize: 18 }
                }}
              />
            }
            label={
              <Typography variant="body1">
                {reason}
              </Typography>
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              m: 0,
              mb: 0.5,
              '& .MuiFormControlLabel-label': {
                ml: 0.5
              }
            }}
          />
        ))}
      </Box>

      {isOtherSelected && (
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          color="secondary"
          placeholder="Tell us what went wrong"
          value={freeText}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length <= 250) {
              setFreeText(newValue);
            }
          }}
          size="medium"
          helperText={`${freeText.length}/250 characters`}
          sx={{
            mb: 1.5
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <NRButtonProgress
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!canSubmit}
          label="Submit"
        />
      </Box>
    </Box>
  );
}
